import React from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '../../components/popover'
import { cn } from '../../lib/utils'

export interface UIPopoverProps extends React.ComponentProps<typeof Popover> {
  variant?: 'default' | 'menu' | 'tooltip'
}

export interface UIPopoverContentProps extends React.ComponentProps<typeof PopoverContent> {
  /**
   * Fixed width sizing. Sizes use Tailwind spacing scale:
   * sm=10rem, md=16rem, lg=20rem, xl=24rem.
   * All sizes are capped at `max-w-[calc(100vw-2rem)]` to prevent viewport overflow.
   */
  size?: 'sm' | 'md' | 'lg' | 'xl'
  variant?: 'default' | 'elevated' | 'minimal' | 'menu'
  spacing?: 'tight' | 'normal' | 'loose'
}

/**
 * Enhanced Popover component following design system best practices
 * Provides consistent popover styling and behavior
 */
export const UIPopover = ({ variant = 'default', ...props }: UIPopoverProps) => {
  return (
    <Popover
      {...props}
    />
  )
}

export const UIPopoverContent = React.forwardRef<
  React.ComponentRef<typeof PopoverContent>,
  UIPopoverContentProps
>(({
  className,
  size = 'md',
  variant = 'default',
  spacing = 'normal',
  align = 'center',
  sideOffset = 4,
  ...props
}, ref) => {
  return (
    <PopoverContent
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        // Base styles
        'z-50 outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',

        // Size styles — fixed widths capped to viewport safety
        'max-w-[calc(100vw-2rem)]',
        {
          'w-40': size === 'sm',
          'w-64': size === 'md',
          'w-80': size === 'lg',
          'w-96': size === 'xl',
        },

        // Variant styles
        {
          'rounded-md border bg-popover text-popover-foreground shadow-md': variant === 'default',
          'rounded-lg border bg-popover text-popover-foreground shadow-lg': variant === 'elevated',
          'rounded-md border-none bg-popover/95 text-popover-foreground shadow-sm': variant === 'minimal',
          'rounded border bg-popover text-popover-foreground shadow-sm': variant === 'menu',
        },

        // Spacing styles
        {
          'p-2': spacing === 'tight',
          'p-4': spacing === 'normal',
          'p-6': spacing === 'loose',
        },

        className
      )}
      {...props}
    />
  )
})

// Re-export other popover components with consistent naming
export const UIPopoverTrigger = PopoverTrigger

UIPopover.displayName = "UIPopover"
UIPopoverContent.displayName = "UIPopoverContent"